import { AppError, AppErrorCode } from '../core/errors';
import type { ElementCategory, ElementDefinition } from './types';

/**
 * Registry for element definitions with lookup and ordering helpers.
 */
export class ElementRegistry {
  private readonly _elements = new Map<string, ElementDefinition>();

  public constructor(elements: ElementDefinition[]) {
    for (const element of elements) {
      this.register(element);
    }
  }

  public register(el: ElementDefinition): void {
    if (this._elements.has(el.id)) {
      throw new AppError(`Duplicate element id: ${el.id}`, AppErrorCode.DUPLICATE_ELEMENT_ID);
    }
    this._elements.set(el.id, el);
  }

  public getAll(): ElementDefinition[] {
    return Array.from(this._elements.values()).sort((a, b) => {
      if (a.category === b.category) {
        return a.label.localeCompare(b.label);
      }
      return a.category.localeCompare(b.category);
    });
  }

  public getByCategory(category: ElementCategory): ElementDefinition[] {
    return this.getAll().filter((element) => element.category === category);
  }

  public getById(id: string): ElementDefinition {
    const found = this._elements.get(id);
    if (!found) {
      throw new AppError(`Element not found: ${id}`, AppErrorCode.ELEMENT_INSERT_FAILED);
    }
    return found;
  }

  public has(id: string): boolean {
    return this._elements.has(id);
  }
}
